import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import {
  Flex, Text, Icon, Segmented, Switch, Calendar, Popover, Drawer,
  useBreakpoint, IconButton, Badge,
  ExpandableSearch, EditableText, List, space,
} from '@nula/core-ui';
import type { TimeGridEvent } from '@nula/core-ui';
import { useCalendarStore } from '@/stores/use-calendar-store';
import type { CalendarEvent } from '@/stores/use-calendar-store';
import { CalendarMonthGrid } from './calendar-month-grid';
import { CalendarEmptyState } from './calendar-empty-state';
import { CalendarConnectionBanner } from './calendar-connection-banner';
import { EventRow } from './event-row';
import { ColorPickerArea } from './color-picker-area';
import { getCalendarColor, CALENDAR_COLOR_PRESETS } from '@/lib/calendar/calendar-colors';
import { localDateKey } from '@/lib/calendar/date-utils';
import { apiFetch } from '@/lib/http/client';
import { useOnVisible } from '@/hooks/use-on-visible';
import styles from './calendar-page.module.css';

const page = cva(styles.page);

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const FILTERS = ['Day', 'Week', 'Month'] as const;

export function CalendarPage() {
  const {
    connections, subscriptions, events, selectedDate,
    fetchConnections, fetchEvents, selectDate, selectEvent, patchSubscription,
  } = useCalendarStore();

  const bp = useBreakpoint();

  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const [weekAnchor, setWeekAnchor] = useState(() => localDateKey(new Date()));
  const [activeFilter, setActiveFilter] = useState<typeof FILTERS[number]>('Day');
  const [searchQuery, setSearchQuery] = useState('');
  const [isTagMenuOpen, setIsTagMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([]);
  const [showWeekends, setShowWeekends] = useState(true);
  const [showEvents, setShowEvents] = useState(true);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [renamingSubId, setRenamingSubId] = useState<string | null>(null);
  const [colorPickerSubId, setColorPickerSubId] = useState<string | null>(null);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const refetchEvents = React.useCallback(() => {
    if (connections.length > 0) {
      const start = new Date(viewYear, viewMonth, 1);
      const end = new Date(viewYear, viewMonth + 1, 0, 23, 59, 59);
      fetchEvents(start, end);
    }
  }, [connections.length, viewYear, viewMonth, fetchEvents]);

  // Week navigation helpers
  const goToPrev = () => {
    if (activeFilter === 'Week') {
      const anchor = new Date(weekAnchor + 'T12:00:00');
      anchor.setDate(anchor.getDate() - 7);
      setWeekAnchor(localDateKey(anchor));
      setViewYear(anchor.getFullYear());
      setViewMonth(anchor.getMonth());
    } else if (activeFilter === 'Day') {
      const anchor = new Date((selectedDate ?? weekAnchor) + 'T12:00:00');
      anchor.setDate(anchor.getDate() - 1);
      const key = localDateKey(anchor);
      selectDate(key);
      setWeekAnchor(key);
      setViewYear(anchor.getFullYear());
      setViewMonth(anchor.getMonth());
    } else {
      goToPrevMonth();
    }
  };

  const goToNext = () => {
    if (activeFilter === 'Week') {
      const anchor = new Date(weekAnchor + 'T12:00:00');
      anchor.setDate(anchor.getDate() + 7);
      setWeekAnchor(localDateKey(anchor));
      setViewYear(anchor.getFullYear());
      setViewMonth(anchor.getMonth());
    } else if (activeFilter === 'Day') {
      const anchor = new Date((selectedDate ?? weekAnchor) + 'T12:00:00');
      anchor.setDate(anchor.getDate() + 1);
      const key = localDateKey(anchor);
      selectDate(key);
      setWeekAnchor(key);
      setViewYear(anchor.getFullYear());
      setViewMonth(anchor.getMonth());
    } else {
      goToNextMonth();
    }
  };

  useEffect(() => {
    refetchEvents();
  }, [refetchEvents]);

  // Refetch on tab focus — picks up archived docs, external sync, etc.
  useOnVisible(refetchEvents);

  const hasConnections = connections.length > 0;

  const goToPrevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const goToToday = useCallback(() => {
    const now = new Date();
    const key = localDateKey(now);
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    setWeekAnchor(key);
    selectDate(key);
  }, [selectDate]);

  // Listen for calendar:nav events fired by useGlobalShortcut (T / J / K / ← / →)
  useEffect(() => {
    function handleCalendarNav(e: Event) {
      const detail = (e as CustomEvent<string>).detail;
      if (detail === 'today') goToToday();
      else if (detail === 'prev') goToPrev();
      else if (detail === 'next') goToNext();
    }
    window.addEventListener('calendar:nav', handleCalendarNav);
    return () => window.removeEventListener('calendar:nav', handleCalendarNav);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goToToday, activeFilter, weekAnchor, selectedDate, viewMonth, viewYear]);


  // Get unique calendar names from events for the filter menu
  const calendarNames = useMemo(() => {
    const names = new Set<string>();
    for (const evts of Object.values(events)) {
      for (const e of evts) names.add(e.subscriptionId);
    }
    return Array.from(names);
  }, [events]);

  const toggleCalendar = (id: string) => {
    setSelectedCalendars(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const connectionIdForSub = (subId: string): string | undefined =>
    subscriptions[subId]?.connectionId;

  // Resolve CSS variable tokens to computed hex for ColorPicker presets
  const commitRenameValue = async (subId: string, value: string) => {
    const trimmed = value.trim();
    const connId = connectionIdForSub(subId);
    if (trimmed && connId && trimmed !== subscriptions[subId]?.name) {
      await patchSubscription(connId, subId, { name: trimmed });
    }
    setRenamingSubId(null);
  };

  const commitColor = async (subId: string, color: string) => {
    const connId = connectionIdForSub(subId);
    if (connId) await patchSubscription(connId, subId, { color });
  };

  // Filter events by search + selected calendars
  const filteredEvents = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const filtered: Record<string, CalendarEvent[]> = {};
    for (const [key, evts] of Object.entries(events)) {
      const f = evts.filter(e => {
        if (q && !e.title.toLowerCase().includes(q)) return false;
        if (selectedCalendars.length > 0 && !selectedCalendars.includes(e.subscriptionId)) return false;
        if (!showEvents && e.title !== 'Busy') return false;
        return true;
      });
      if (f.length > 0) filtered[key] = f;
    }
    return filtered;
  }, [events, searchQuery, selectedCalendars, showEvents]);

  // Convert filtered events → TimeGridEvent[] for the week/day time-grid
  const timeGridEvents = useMemo((): TimeGridEvent[] => {
    const result: TimeGridEvent[] = [];
    for (const dayEvents of Object.values(filteredEvents)) {
      for (const e of dayEvents) {
        const start = new Date(e.startAt);
        const end = new Date(e.endAt);
        result.push({
          id: e.id,
          date: localDateKey(start),
          startMinute: start.getHours() * 60 + start.getMinutes(),
          endMinute: Math.max(end.getHours() * 60 + end.getMinutes(), start.getHours() * 60 + start.getMinutes() + 30),
          title: e.title,
          color: getCalendarColor(e.subscriptionId, subscriptions[e.subscriptionId]?.color),
          isAllDay: e.isAllDay,
          onClick: () => selectEvent(e),
        });
      }
    }
    return result;
  }, [filteredEvents, selectEvent, subscriptions]);

  return (
    <Flex vertical className={page()}>
      <Flex vertical className={styles.inner}>
        <Flex vertical className={styles.contentWrap}>
          <CalendarConnectionBanner connections={connections} />

          {/* ── Header ── */}
          <Flex vertical className={styles.headerSection}>
            <Flex
              justify="space-between"
              align="end"
              gap="lg"
              className={clsx(styles.headerTopRow)}
            >
              {/* Title */}
              <Flex align="center" gap="sm" className={styles.titleMotion}>
                <Flex align="center" justify="center" className={styles.titleIconWrap}>
                  <Icon name="calendar_month" size={20} weight={300} className={styles.titleIcon} />
                </Flex>
                <Text className={styles.pageTitle}>
                  Calendar
                </Text>
              </Flex>

              {/* Action buttons */}
              <Flex>
                <Flex align="center" gap={space[3]} className={styles.actionBar}>
                  {/* Search */}
                  <ExpandableSearch
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onClear={() => setSearchQuery('')}
                    label="Search calendar…"
                    buttonSize="lg"
                    elevated
                  />

                  {/* Calendar filter dropdown */}
                  {bp.md ? (
                    <Popover
                      open={isTagMenuOpen}
                      onOpenChange={(open) => { if (!open) setColorPickerSubId(null); setIsTagMenuOpen(open); }}
                      trigger="click"
                      placement="bottom-end"
                      arrow={false}
                      content={
                        <Flex vertical gap={0} className={styles.filterDropdown}>
                          <Flex align="center" justify="space-between" className={styles.filterHeader}>
                            <Text className={styles.filterLabel}>
                              Filter by Calendar
                            </Text>
                            {selectedCalendars.length > 0 && (
                              <Text
                                size="xs"
                                onClick={() => setSelectedCalendars([])}
                                className={styles.filterClearBtn}
                              >
                                Clear
                              </Text>
                            )}
                          </Flex>
                          <Flex vertical gap="xs">
                            {calendarNames.map(subId => {
                              const isActive = selectedCalendars.includes(subId);
                              const sub = subscriptions[subId];
                              const color = getCalendarColor(subId, sub?.color);
                              const isRenaming = renamingSubId === subId;
                              const isColorOpen = colorPickerSubId === subId;
                              return (
                                <Flex key={subId} vertical gap={0}>
                                  <Flex
                                    align="center"
                                    justify="space-between"
                                    className={clsx(
                                      styles.calendarRow,
                                      isActive ? styles.calendarRowActive : styles.calendarRowInactive,
                                    )}
                                    style={{ '--cal-color': color } as React.CSSProperties}
                                  >
                                    {/* Color dot — click to open picker */}
                                    <Flex
                                      align="center"
                                      gap="sm"
                                      className={styles.calendarRowInner}
                                      onClick={() => toggleCalendar(subId)}
                                    >
                                      <Flex
                                        onClick={e => { e.stopPropagation(); setColorPickerSubId(isColorOpen ? null : subId); }}
                                        className={clsx(
                                          styles.calColorDot,
                                          isColorOpen && styles.calColorDotOpen,
                                        )}
                                        style={{ '--cal-color': color } as React.CSSProperties}
                                      />
                                      <EditableText
                                        value={sub?.name ?? subId}
                                        onChange={val => { void commitRenameValue(subId, val); }}
                                        onCancel={() => setRenamingSubId(null)}
                                        editing={isRenaming}
                                        onEditingChange={open => { if (!open) setRenamingSubId(null); }}
                                        size="sm"
                                        className={clsx(
                                          styles.calendarName,
                                          styles.flex1,
                                          isActive ? styles.calendarNameActive : styles.calendarNameInactive,
                                        )}
                                      />
                                    </Flex>
                                    <Flex align="center" gap="xs">
                                      {/* Rename button */}
                                      <IconButton
                                        size="sm"
                                        aria-label="Rename calendar"
                                        onClick={e => {
                                          e.stopPropagation();
                                          setRenamingSubId(isRenaming ? null : subId);
                                        }}
                                        className={clsx(
                                          styles.renameBtn,
                                          isRenaming ? styles.renameBtnActive : styles.renameBtnInactive,
                                        )}
                                      >
                                        <Icon name="edit" size={12} />
                                      </IconButton>
                                      {isActive && <Icon name="check" size={14} className={styles.checkIcon} />}
                                    </Flex>
                                  </Flex>

                                  {/* Color picker — pastel swatches + free picker */}
                                  {isColorOpen && (
                                    <ColorPickerArea
                                      defaultValue={sub?.color ?? ''}
                                      onChange={color => commitColor(subId, color)}
                                      colors={CALENDAR_COLOR_PRESETS.map(p => p.value)}
                                      indent="sm"
                                    />
                                  )}
                                </Flex>
                              );
                            })}
                          </Flex>
                        </Flex>
                      }
                    >
                      <Badge count={selectedCalendars.length} showZero={false} color="var(--primary)" offset={[2, 2]}>
                        <IconButton
                          size="lg"
                          aria-label="Filter by calendar"
                          elevated
                          className={clsx(
                            selectedCalendars.length > 0 || isTagMenuOpen
                              ? styles.filterBtnActive
                              : styles.filterBtnDefault,
                          )}
                        >
                          <Icon name="sell" size={16} />
                        </IconButton>
                      </Badge>
                    </Popover>
                  ) : (
                    <Badge count={selectedCalendars.length} showZero={false} color="var(--primary)" offset={[2, 2]}>
                      <IconButton
                        size="lg"
                        aria-label="Filter by calendar"
                        onClick={() => setIsTagMenuOpen(true)}
                        className={clsx(
                          styles.filterBtn,
                          selectedCalendars.length > 0 || isTagMenuOpen
                            ? styles.filterBtnActive
                            : styles.filterBtnDefault,
                        )}
                      >
                        <Icon name="sell" size={16} />
                      </IconButton>
                    </Badge>
                  )}

                  {/* Settings dropdown */}
                  {bp.md ? (
                    <Popover
                      open={isSettingsOpen}
                      onOpenChange={setIsSettingsOpen}
                      trigger="click"
                      placement="bottom-end"
                      arrow={false}
                      content={
                        <Flex vertical gap="xs" className={styles.settingsDropdown}>
                          <Text className={styles.settingsSectionLabel}>
                            Calendar Settings
                          </Text>
                          <List.Item
                            trailing={<Switch checked={showWeekends} onChange={setShowWeekends} size="sm" />}
                            onClick={() => setShowWeekends(!showWeekends)}
                            size="sm"
                            className={styles.settingsRow}
                          ><Text size="sm">Show Weekends</Text></List.Item>
                          <List.Item
                            leading={<Icon name="event" size={14} className={styles.settingsIcon} />}
                            trailing={<Switch checked={showEvents} onChange={setShowEvents} size="sm" />}
                            onClick={() => setShowEvents(!showEvents)}
                            size="sm"
                            className={styles.settingsRow}
                          ><Text size="sm">Events</Text></List.Item>
                          <Flex className={styles.settingsDivider} />
                          <List.Item
                            leading={
                              <Icon
                                name="sync"
                                size={14}
                                className={isSyncing ? styles.syncIconSpinning : styles.syncIcon}
                              />
                            }
                            onClick={async () => {
                              if (isSyncing) return;
                              setIsSyncing(true);
                              try {
                                await Promise.all(connections.map(conn =>
                                  apiFetch(`/workspace/integrations/calendar/connections/${conn.id}/sync`, { method: 'POST' }).catch(() => {})
                                ));
                                const start = new Date(viewYear, viewMonth, 1);
                                const end = new Date(viewYear, viewMonth + 1, 0, 23, 59, 59);
                                await fetchEvents(start, end);
                              } finally {
                                setIsSyncing(false);
                              }
                            }}
                            size="sm"
                            className={clsx(
                              styles.settingsRow,
                              isSyncing && styles.settingsRowDisabled,
                            )}
                          ><Text size="sm">{isSyncing ? 'Syncing…' : 'Sync External'}</Text></List.Item>
                        </Flex>
                      }
                    >
                      <IconButton
                        size="lg"
                        aria-label="Calendar settings"
                        elevated
                        className={clsx(
                          isSettingsOpen ? styles.settingsBtnActive : styles.settingsBtnDefault,
                        )}
                      >
                        <Icon name="more_horiz" size={18} />
                      </IconButton>
                    </Popover>
                  ) : (
                    <IconButton
                      size="lg"
                      aria-label="Calendar settings"
                      onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                      className={clsx(
                        styles.settingsBtn,
                        isSettingsOpen ? styles.settingsBtnActive : styles.settingsBtnDefault,
                      )}
                    >
                      <Icon name="more_horiz" size={18} />
                    </IconButton>
                  )}

                </Flex>
              </Flex>
            </Flex>

            {/* ── Controls row: filters + navigation ── */}
            <Flex vertical>
              <Flex
                align={bp.md ? 'center' : 'stretch'}
                justify={bp.md ? 'space-between' : undefined}
                vertical={!bp.md}
                gap="md"
              >
                {/* Type filters */}
                <Segmented
                  options={[...FILTERS]}
                  value={activeFilter}
                  onChange={v => setActiveFilter(v as typeof FILTERS[number])}
                  ghost
                />

                {/* Month navigation */}
                <Flex
                  align="center"
                  gap="md"
                  className={clsx(styles.navBar, !bp.md && styles.navBarMobile)}
                >
                  {/* Prev */}
                  <IconButton
                    size="sm"
                    aria-label="Previous"
                    onClick={goToPrev}
                    className={styles.navBtn}
                  >
                    <Icon name="chevron_left" size={18} />
                  </IconButton>

                  {/* Date label — click to open mini picker */}
                  <Popover
                    open={isDatePickerOpen}
                    onOpenChange={setIsDatePickerOpen}
                    trigger="click"
                    placement="bottom"
                    content={
                      <Calendar
                        variant="picker"
                        fullscreen={false}
                        weekPicker={activeFilter === 'Week'}
                        value={
                          activeFilter === 'Day' && selectedDate
                            ? selectedDate
                            : activeFilter === 'Week'
                              ? weekAnchor
                              : `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-01`
                        }
                        onChange={date => {
                          const d = new Date(date + 'T12:00:00');
                          setViewYear(d.getFullYear());
                          setViewMonth(d.getMonth());
                          setWeekAnchor(date);
                          selectDate(date);
                          setIsDatePickerOpen(false);
                        }}
                        className={styles.pickerCalendar}
                      />
                    }
                  >
                    <Flex
                      align="center"
                      justify="center"
                      role="button"
                      className={clsx(
                        styles.dateLabelBtn,
                        isDatePickerOpen ? styles.dateLabelBtnActive : styles.dateLabelBtnDefault,
                        bp.md ? styles.dateLabelBtnDesktop : styles.dateLabelBtnMobile,
                      )}
                    >
                      <Text size="sm" className={styles.dateLabelText}>
                        {activeFilter === 'Week' ? (() => {
                          const anchor = new Date(weekAnchor + 'T12:00:00');
                          const sun = new Date(anchor); sun.setDate(anchor.getDate() - anchor.getDay());
                          const sat = new Date(sun); sat.setDate(sun.getDate() + 6);
                          return sun.getMonth() === sat.getMonth()
                            ? `${MONTH_NAMES[sun.getMonth()]} ${sun.getDate()}–${sat.getDate()}`
                            : `${MONTH_NAMES[sun.getMonth()]} ${sun.getDate()} – ${MONTH_NAMES[sat.getMonth()]} ${sat.getDate()}`;
                        })() : activeFilter === 'Day' && selectedDate
                          ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                          : `${MONTH_NAMES[viewMonth]} ${viewYear}`}
                      </Text>
                    </Flex>
                  </Popover>

                  {/* Next */}
                  <IconButton
                    size="sm"
                    aria-label="Next"
                    onClick={goToNext}
                    className={styles.navBtn}
                  >
                    <Icon name="chevron_right" size={18} />
                  </IconButton>

                  <Flex className={styles.navDivider} />

                  {/* Today */}
                  <Flex
                    align="center"
                    justify="center"
                    onClick={goToToday}
                    role="button"
                    className={styles.todayBtn}
                  >
                    <Text size="sm" className={styles.todayBtnLabel}>Today</Text>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          {/* ── Body ── */}
          {!hasConnections ? (
            <CalendarEmptyState onConnected={fetchConnections} />
          ) : (
            <Flex vertical className={styles.bodyMotion}>
              {/* Glassmorphic grid container */}
              <Flex vertical className={styles.glassContainer}>
                {/* Ambient glows */}
                <Flex className={styles.ambientGlowTop} />
                <Flex className={styles.ambientGlowBottom} />

                {activeFilter === 'Month' && !bp.md && (
                  <Flex vertical className={clsx(styles.mobileEventList, 'hide-scrollbar')}>
                    {(() => {
                      const monthKey = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
                      const days = Object.entries(filteredEvents)
                        .filter(([k]) => k.startsWith(monthKey))
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([dateKey, evts]) => ({
                          dateKey,
                          dayEvents: [...evts].sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()),
                        }))
                        .filter(({ dayEvents }) => dayEvents.length > 0);

                      if (days.length === 0) return (
                        <Flex align="center" gap="sm" className={styles.mobileEmptyRow}>
                          <Icon name="calendar_month" size={16} className={styles.mobileEmptyIcon} />
                          <Text size="sm" type="secondary">No events this month</Text>
                        </Flex>
                      );

                      return days.map(({ dateKey, dayEvents }) => {
                        const d = new Date(dateKey + 'T12:00:00');
                        const isToday = dateKey === localDateKey(new Date());
                        const dayLabel = isToday ? 'Today' : new Intl.DateTimeFormat(navigator.language, { weekday: 'short', month: 'short', day: 'numeric' }).format(d);
                        return (
                          <Flex key={dateKey} vertical gap="xs" className={styles.mobileDayGroup}>
                            <Text size="xs" className={clsx(
                              styles.mobileDayHeader,
                              isToday ? styles.mobileDayHeaderToday : styles.mobileDayHeaderDefault,
                            )}>{dayLabel}</Text>
                            {dayEvents.map(event => {
                              const start = new Date(event.startAt);
                              const color = getCalendarColor(event.subscriptionId, subscriptions[event.subscriptionId]?.color);
                              const timeLabel = event.isAllDay ? 'All day' : start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                              return (
                                <EventRow
                                  key={event.id}
                                  time={timeLabel}
                                  title={event.title}
                                  color={color}
                                  onClick={() => selectEvent(event)}
                                />
                              );
                            })}
                          </Flex>
                        );
                      });
                    })()}
                  </Flex>
                )}
                {activeFilter === 'Month' && bp.md && (
                  <Flex vertical className={clsx(styles.monthGridScroll, 'hide-scrollbar')}>
                    <CalendarMonthGrid
                      year={viewYear}
                      month={viewMonth}
                      events={filteredEvents}
                      selectedDate={selectedDate}
                      onSelectDate={date => { selectDate(date); setWeekAnchor(date); }}
                      onSelectEvent={selectEvent}
                      showWeekends={showWeekends}
                    />
                  </Flex>
                )}
                {activeFilter === 'Week' && (
                  <Calendar
                    variant="week"
                    value={weekAnchor}
                    onChange={date => { selectDate(date); setWeekAnchor(date); }}
                    timeEvents={timeGridEvents}
                    showWeekends={bp.md ? showWeekends : false}
                    className={styles.calendarGrid}
                  />
                )}
                {activeFilter === 'Day' && (
                  <Calendar
                    variant="day"
                    value={selectedDate ?? weekAnchor}
                    onChange={date => { selectDate(date); setWeekAnchor(date); }}
                    timeEvents={timeGridEvents}
                    className={styles.calendarGrid}
                  />
                )}
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* ── Mobile: Filter drawer ── */}
      {!bp.md && (
        <Drawer
          open={isTagMenuOpen}
          onClose={() => setIsTagMenuOpen(false)}
          placement="bottom"
          width="100vw"
          title="Filter by Calendar"
        >
          <Flex vertical gap="xs">
            {selectedCalendars.length > 0 && (
              <Flex justify="end" className={styles.mobileFilterClearRow}>
                <Text size="xs" onClick={() => setSelectedCalendars([])} className={styles.filterClearBtn}>Clear</Text>
              </Flex>
            )}
            {calendarNames.map(subId => {
              const isActive = selectedCalendars.includes(subId);
              const sub = subscriptions[subId];
              const color = getCalendarColor(subId, sub?.color);
              const isRenaming = renamingSubId === subId;
              const isColorOpen = colorPickerSubId === subId;
              return (
                <Flex key={subId} vertical gap={0}>
                  <Flex
                    align="center"
                    justify="space-between"
                    className={clsx(
                      styles.mobileCalendarRow,
                      isActive && styles.mobileCalendarRowActive,
                    )}
                  >
                    <Flex align="center" gap="sm" className={styles.flexInner} onClick={() => toggleCalendar(subId)}>
                      <Flex
                        onClick={e => { e.stopPropagation(); setColorPickerSubId(isColorOpen ? null : subId); }}
                        className={clsx(
                          styles.mobileCalColorDot,
                          isColorOpen && styles.mobileCalColorDotOpen,
                        )}
                        style={{ '--cal-color': color } as React.CSSProperties}
                      />
                      <EditableText
                        value={sub?.name ?? subId}
                        onChange={val => { void commitRenameValue(subId, val); }}
                        onCancel={() => setRenamingSubId(null)}
                        editing={isRenaming}
                        onEditingChange={open => { if (!open) setRenamingSubId(null); }}
                        size="sm"
                        className={clsx(
                          styles.mobileCalendarName,
                          styles.flex1,
                          isActive ? styles.mobileCalendarNameActive : styles.mobileCalendarNameInactive,
                        )}
                      />
                    </Flex>
                    <Flex align="center" gap="sm">
                      <IconButton
                        size="sm"
                        aria-label="Rename calendar"
                        onClick={e => { e.stopPropagation(); setRenamingSubId(isRenaming ? null : subId); }}
                        className={clsx(
                          styles.mobileRenameBtn,
                          isRenaming ? styles.mobileRenameBtnActive : styles.mobileRenameBtnInactive,
                        )}
                      >
                        <Icon name="edit" size={14} />
                      </IconButton>
                      {isActive && <Icon name="check" size={16} className={styles.checkIcon} />}
                    </Flex>
                  </Flex>
                  {isColorOpen && (
                    <ColorPickerArea
                      defaultValue={sub?.color ?? ''}
                      onChange={color => commitColor(subId, color)}
                      colors={CALENDAR_COLOR_PRESETS.map(p => p.value)}
                      indent="md"
                    />
                  )}
                </Flex>
              );
            })}
          </Flex>
        </Drawer>
      )}

      {/* ── Mobile: Settings drawer ── */}
      {!bp.md && (
        <Drawer
          open={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          placement="bottom"
          width="100vw"
          title="Calendar Settings"
        >
          <Flex vertical gap="xs">
            <List.Item
              trailing={<Switch checked={showWeekends} onChange={setShowWeekends} size="sm" />}
              onClick={() => setShowWeekends(!showWeekends)}
              size="sm"
              className={styles.mobileSettingsRow}
            ><Text size="sm">Show Weekends</Text></List.Item>
            <List.Item
              leading={<Icon name="event" size={14} className={styles.settingsIcon} />}
              trailing={<Switch checked={showEvents} onChange={setShowEvents} size="sm" />}
              onClick={() => setShowEvents(!showEvents)}
              size="sm"
              className={styles.mobileSettingsRow}
            ><Text size="sm">Events</Text></List.Item>
            <Flex className={styles.mobileSettingsDivider} />
            <List.Item
              leading={<Icon name="sync" size={14} className={isSyncing ? styles.syncIconSpinning : styles.syncIcon} />}
              onClick={async () => {
                if (isSyncing) return;
                setIsSyncing(true);
                try {
                  await Promise.all(connections.map(conn =>
                    apiFetch(`/workspace/integrations/calendar/connections/${conn.id}/sync`, { method: 'POST' }).catch(() => {})
                  ));
                  const start = new Date(viewYear, viewMonth, 1);
                  const end = new Date(viewYear, viewMonth + 1, 0, 23, 59, 59);
                  await fetchEvents(start, end);
                } finally {
                  setIsSyncing(false);
                }
              }}
              size="sm"
              className={clsx(styles.mobileSyncRow, isSyncing && styles.mobileSyncRowDisabled)}
            ><Text size="sm">{isSyncing ? 'Syncing…' : 'Sync External'}</Text></List.Item>
          </Flex>
        </Drawer>
      )}
    </Flex>
  );
}
